import { EmptyState, Spinner } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useEffect, useRef } from 'react';
import { Waypoint } from 'react-waypoint';

import { auditLogsApi } from 'apis/auditLogs';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';

import styles from './activityLog.module.scss';
import { AiDraftActivityEntry } from './aiDraftActivityTypes/aiDraftActivityEntry';

type AiDraftActivityLogProps = {
  /** The AI article suggestion whose history is shown. */
  readonly aiDraftId: string;
  /**
   * Changes whenever the draft is known to have changed, so the log reads
   * its history again. The suggestions queue passes its live-update count.
   */
  readonly revision?: number;
};

/**
 * The history of one AI article suggestion, newest first: who wrote and
 * revised it, with which AI settings, and how it left the queue. Shown inside
 * the knowledge lib's suggestion preview, which cannot reach the app's APIs.
 */
export function AiDraftActivityLog({
  aiDraftId,
  revision,
}: AiDraftActivityLogProps): JSX.Element {
  const {
    items: entries,
    changeArg,
    asyncStatus,
    hasNextPage,
    nextPage,
    refetchItems,
  } = useLazyPaginatedEndpoint({
    endpoint: auditLogsApi.endpoints.readKnowledgeAiDraftAuditLogs,
    itemsSelector: (response) => response.data.logs,
    infinite: true,
  });

  useEffect(() => {
    changeArg({
      resourceId: aiDraftId,
      limit: 100,
      sort: { key: 'timestamp', direction: 'desc' },
    });
  }, [changeArg, aiDraftId]);

  // The same arguments again would not fetch anew, so a change to the draft
  // asks for its history outright. Not on the first render: `changeArg` has
  // just asked for it.
  const loadedRevision = useRef(revision);
  useEffect(() => {
    if (revision === loadedRevision.current) return;
    loadedRevision.current = revision;
    refetchItems();
  }, [revision, refetchItems]);

  return (
    <div
      className={classnames({
        [styles.root]: true,
        [styles.hasLogs]: entries.length,
      })}
    >
      {asyncStatus === 'rejected' && (
        <EmptyState
          icon="exclamation-triangle-filled"
          title="Failed to retrieve the activity log"
          description="Something went wrong while loading this suggestion's history."
        />
      )}

      {entries.map((entry) => (
        <AiDraftActivityEntry key={entry.event_id} entry={entry} />
      ))}

      {asyncStatus === 'fulfilled' && !entries.length && (
        <EmptyState
          icon="file-text-search"
          title="No activity yet"
          description="Changes to this suggestion will show up here."
        />
      )}

      {(asyncStatus === 'pending' || asyncStatus === 'uninitialized') && (
        <div className={styles.spinnerContainer} data-testid="spinner">
          <Spinner />
        </div>
      )}

      {asyncStatus === 'fulfilled' && hasNextPage && (
        <Waypoint onEnter={nextPage} />
      )}
    </div>
  );
}
